import { type ReactNode, useCallback, useState } from "react";
import { ConfirmModal } from "../components/ConfirmModal";
import {
  Count,
  type DisconnectImpact,
  DisconnectWarning,
} from "../components/DisconnectWarning";
import { useGroupRemovalDisconnectImpact } from "./useGroupRemovalDisconnectImpact";

// Guards a group-membership removal behind a confirmation when the removed
// member would lose connector access that workflows or git sync in their
// spaces rely on. Removals with no visible impact proceed silently.
export function useGroupRemovalDisconnectGuard({
  userGroupIds,
}: {
  userGroupIds: readonly string[];
}): {
  guard: (
    groupId: string,
    removedUserIds: readonly string[],
    proceed: () => void
  ) => void;
  modal: ReactNode;
} {
  const computeImpact = useGroupRemovalDisconnectImpact(userGroupIds);
  const [pending, setPending] = useState<{
    proceed: () => void;
    impact: DisconnectImpact;
  } | null>(null);

  const guard = useCallback(
    (
      groupId: string,
      removedUserIds: readonly string[],
      proceed: () => void
    ) => {
      const impact = computeImpact(groupId, removedUserIds);
      if (impact.workflows.length === 0 && impact.gitSpaceNames.length === 0) {
        proceed();
        return;
      }
      setPending({ proceed, impact });
    },
    [computeImpact]
  );

  const workflowCount = pending?.impact.workflows.length ?? 0;
  const hasWorkflows = workflowCount > 0;
  const modal = (
    <ConfirmModal
      open={pending !== null}
      onClose={() => setPending(null)}
      onConfirm={() => {
        pending?.proceed();
        setPending(null);
      }}
      title="Remove from group?"
      description={
        hasWorkflows ? (
          <>
            Removing the member means{" "}
            <Count n={workflowCount} noun="workflow" /> will need a connector
            replaced.
          </>
        ) : (
          "This group provides connector access git sync may rely on. Removing the member removes that access."
        )
      }
      confirmLabel="Continue"
      destructive
    >
      {pending && <DisconnectWarning {...pending.impact} />}
    </ConfirmModal>
  );

  return { guard, modal };
}
